import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  SmartToy as SmartToyIcon
} from '@mui/icons-material';
import Markdown from 'react-markdown';
import apiService from '../../services/api';
import useStopwatch, { formatElapsed } from '../../hooks/useStopwatch';
import NeuroMetricsChips from './NeuroMetricsChips';

/**
 * Вкладка «Чат»: тест чата с нейросетью
 * (аналог чата в мобильном приложении — текст или текст+фото).
 */
const NeuroChatTester = ({ onRequestDone }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const { elapsedMs, start, stop } = useStopwatch();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFile(selected);
    setFilePreviewUrl(URL.createObjectURL(selected));
    event.target.value = '';
  };

  const handleRemoveFile = () => {
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFile(null);
    setFilePreviewUrl(null);
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    const userMessage = {
      role: 'user',
      text: trimmed,
      imagePreviewUrl: file ? filePreviewUrl : null
    };
    setMessages((prev) => [...prev, userMessage]);
    setText('');
    setError(null);

    const sendingFile = file;
    setFile(null);
    setFilePreviewUrl(null);

    setSending(true);
    start();

    try {
      const formData = new FormData();
      formData.append('text', trimmed);
      if (sendingFile) formData.append('image', sendingFile);

      const response = await apiService.neuro.testChat(formData);
      const data = response.data?.data || {};

      const aiMessage = {
        role: 'ai',
        text: data.response || '',
        metrics: {
          timeMs: data.responseTime,
          inputTokens: data.usage?.prompt_tokens ?? data.cost?.inputTokens,
          outputTokens: data.usage?.completion_tokens ?? data.cost?.outputTokens,
          totalTokens: data.usage?.total_tokens,
          costUsd: data.cost?.usd,
          model: data.model || data.cost?.model
        }
      };
      setMessages((prev) => [...prev, aiMessage]);
      if (onRequestDone) onRequestDone();
    } catch (err) {
      console.error('Ошибка тестового чата:', err);
      const message = err.response?.data?.message || err.message;
      setError(message);
      setMessages((prev) => [...prev, { role: 'error', text: message }]);
    } finally {
      stop();
      setSending(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Тест чата</Typography>

        {/* Лента сообщений */}
        <Box
          sx={{
            maxHeight: 480,
            minHeight: 200,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            mb: 2
          }}
        >
          {messages.length === 0 && !sending ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              Отправьте сообщение или фото с вопросом — как в мобильном приложении
            </Typography>
          ) : (
            messages.map((message, index) => {
              if (message.role === 'user') {
                return (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Box sx={{ maxWidth: '75%' }}>
                      <Box
                        sx={{
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                          borderRadius: 2,
                          px: 2,
                          py: 1
                        }}
                      >
                        {message.imagePreviewUrl && (
                          <Box
                            component="img"
                            src={message.imagePreviewUrl}
                            alt="Вложение"
                            sx={{ maxHeight: 160, maxWidth: '100%', borderRadius: 1, display: 'block', mb: 1 }}
                          />
                        )}
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.text}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              }

              if (message.role === 'error') {
                return (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                    <Alert severity="error">{message.text}</Alert>
                  </Box>
                );
              }

              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'info.main' }}>
                    <SmartToyIcon />
                  </Avatar>
                  <Box sx={{ maxWidth: '75%' }}>
                    <Box
                      sx={{
                        bgcolor: 'grey.100',
                        borderRadius: 2,
                        px: 2,
                        py: 1
                      }}
                    >
                      <Markdown>{message.text}</Markdown>
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      <NeuroMetricsChips {...message.metrics} />
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}

          {/* Пузырь ожидания */}
          {sending && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'info.main' }}>
                <SmartToyIcon />
              </Avatar>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                  px: 2,
                  py: 1
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  Нейросеть думает… {formatElapsed(elapsedMs)}
                </Typography>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* Поле ввода */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />

        {file && (
          <Box sx={{ mb: 1 }}>
            <Chip
              label={file.name}
              size="small"
              variant="outlined"
              onDelete={handleRemoveFile}
              deleteIcon={<CloseIcon />}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <Tooltip title="Прикрепить фото">
            <span>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
              >
                <AttachFileIcon />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Введите сообщение..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            variant="contained"
            disabled={!text.trim() || sending}
            onClick={handleSend}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            {sending ? <CircularProgress size={20} /> : <SendIcon />}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Текст обязателен — фото без текста не отправляется. Enter — отправка, Shift+Enter — новая строка.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default NeuroChatTester;
